import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cleanDisplayValue } from '../../utils/displayUtils';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Badge,
  InputAdornment,
  Paper,
  Alert,
  LinearProgress,
  Divider,
  Stack,
  CircularProgress,
  Link,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import './AssignedTasks.css';
import './client-css/AssignedTasks-light.css';
import './client-css/AssignedTasks-dark.css';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  CheckCircle as CompletedIcon,
  Schedule as PendingIcon,
  Cancel as CancelIcon,
  Assignment as TaskIcon,
  Person as PersonIcon,
  CalendarToday as DateIcon,
  Close as CloseIcon,
  Message as MessageIcon,
  PlayArrow as InProgressIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
  AccessTime as TimeIcon,
  TrendingUp as ProgressIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../utils/apiUrl';
import SuccessDialog from '../../components/SuccessDialog';
import { clientAPI } from '../../utils/api';

interface Assignment {
  _id: string;
  assignmentId: string;
  title: string;
  description: string;
  assignedTo: {
    _id: string;
    clientName: string;
    username: string;
    email: string;
    role: string;
  };
  assignedBy: {
    _id: string;
    clientName: string;
    username: string;
  };
  companyId: {
    _id: string;
    companyName: string;
  };
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  category: 'lead_processing' | 'data_analysis' | 'data_entry' | 'review' | 'research' | 'other';
  dueDate?: string;
  completedDate?: string;
  assignmentNotes?: string;
  completionNotes?: string;
  companies: Array<{
    companyId: string;
    companyName: string;
    country: string;
    status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
    startedAt?: string;
    completedAt?: string;
    notes?: string;
    completedLeads?: number;
    totalLeads?: number;
  }>;
  totalCompanies: number;
  completedCompanies: number;
  completionPercentage: number;
  // NEW: Lead-level completion statistics
  completedLeads?: number;
  totalLeads?: number;
  remainingLeads?: number;
  hasCompletedLeads?: boolean;
  isFullyCompleted?: boolean;
  estimatedHours?: number;
  actualHours?: number;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  progressStatus: string;
  isOverdue: boolean;
  completedBy?: {
    username: string;
    clientName: string;
  } | null;
  leadFullName?: string;
  leadPhone?: string;
  leadWorkEmail?: string;
}

interface Lead {
  leadId: string;
  originalData: any;
  newContactStatus: string;
  newCallOutcome: string;
  newLeadQualification: string;
  _isCompleted: boolean;
  _completedBy?: string;
  _completedAt?: string;
  prevContactStatus?: string;
  prevCallOutcome?: string;
  prevLeadQualification?: string;
}

interface CompletedLead {
  _id: string;
  leadId: string;
  assignmentId: string;
  assignmentTitle: string;
  assignmentObjectId: string;
  leadCompanyName: string;
  companyName: string;
  companies?: any[];
  assignedTo: {
    _id: string;
    clientName: string;
    username: string;
    role: string;
  } | null;
  assignedBy: {
    _id: string;
    clientName: string;
    username: string;
  } | null;
  contactStatus: string;
  callOutcome: string;
  leadQualification: string;
  completedBy: {
    username: string;
    clientName: string;
  } | null;
  completedAt: string;
  originalData: any;
  status: string;
  category: string;
  priority: string;
  createdAt: string;
  dueDate?: string;
  leadFullName?: string;
  leadPhone?: string;
  leadWorkEmail?: string;
}

const AssignedTasks: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Get theme mode for CSS class selection
  const isDarkMode = theme.palette.mode === 'dark';
  
  // Success Dialog state
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState({
    title: '',
    message: '',
    autoCloseDelay: 3000,
  });
  
  // Check if user is viewer (read-only mode)
  const isViewer = user?.role === 'viewer';
  const isSales = user?.role === 'editor';
  const isAdmin = user?.role === 'admin';

  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [completedAssignments, setCompletedAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [selectedAssignments, setSelectedAssignments] = useState<GridRowSelectionModel>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [dataSheetDialogOpen, setDataSheetDialogOpen] = useState(false);
  const [completedWorkData, setCompletedWorkData] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<'admin' | 'sales'>('admin');
  const [currentTab, setCurrentTab] = useState(0);
  const [companyLeadsDialogOpen, setCompanyLeadsDialogOpen] = useState(false);
  const [selectedCompanyLeads, setSelectedCompanyLeads] = useState<any[]>([]);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string>('');
  
  // NEW: State for expanded rows and lead details
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [leadsData, setLeadsData] = useState<Record<string, Lead[]>>({});
  const [loadingLeads, setLoadingLeads] = useState<Record<string, boolean>>({});
  
  // State for individual completed leads
  const [completedLeads, setCompletedLeads] = useState<CompletedLead[]>([]);
  const [loadingCompletedLeads, setLoadingCompletedLeads] = useState(false);
  
  // Check URL parameters for tab selection
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam === '1') {
      setCurrentTab(1); // Set to current assignments tab
    }
  }, []); // 0 = completed, 1 = current

  const fetchAssignments = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching assignments for user role:', user?.role, 'isViewer:', isViewer);
      
      let response;
      
      // Use viewer-specific endpoint for viewers, regular endpoint for others
      if (isViewer) {
        console.log('📡 Calling viewer assignments endpoint...');
        response = await clientAPI.getViewerAssignments();
        console.log('📡 Viewer assignments response:', response);
      } else {
        console.log('📡 Calling regular assignments endpoint...');
        response = await clientAPI.getAssignments({
          status: statusFilter || undefined,
          page: 1,
          limit: 100
        });
        console.log('📡 Regular assignments response:', response);
      }

      if (response.data.success) {
        console.log('✅ Assignments fetched successfully, data:', response.data.data);
        console.log('🔍 All assignment statuses:', response.data.data.map((a: Assignment) => ({ 
          _id: a._id,
          assignmentId: a.assignmentId, 
          status: a.status, 
          title: a.title 
        })));
        
        console.log('🔍 Sample assignment object structure:', response.data.data[0]);
        
        // For current assignments tab, filter out completed assignments
        const activeAssignments = response.data.data.filter((assignment: Assignment) => 
          assignment.status !== 'completed'
        );
        console.log('📋 Active assignments:', activeAssignments);
        console.log('🔍 Filtered out completed assignments:', response.data.data.filter((a: Assignment) => a.status === 'completed'));
        
        // For viewers, we already have all assignments from getViewerAssignments()
        // For other roles, fetch completed assignments separately
        if (!isViewer) {
          try {
            const completedResponse = await clientAPI.getAssignments({
              status: 'completed',
              page: 1,
              limit: 100
            });
            if (completedResponse.data.success) {
              const completedAssignments = (completedResponse.data.data || []).filter((assignment: Assignment) => 
                assignment.status === 'completed'
              );
              console.log('📋 Completed assignments (synced):', completedAssignments);
              setCompletedAssignments(completedAssignments);
            }
          } catch (completedError) {
            console.log('⚠️ Could not fetch completed assignments for sync:', completedError);
          }
        } else {
          // For viewers, we already have all assignments, so set completed assignments from the same data
          const completedAssignments = response.data.data.filter((assignment: Assignment) => 
            assignment.status === 'completed'
          );
          console.log('📋 Viewer completed assignments:', completedAssignments);
          setCompletedAssignments(completedAssignments);
        }
        
        setAssignments(activeAssignments);
      } else {
        console.error('❌ API returned success: false', response.data);
        throw new Error(response.data.message || 'Failed to fetch assignments');
      }
    } catch (error: any) {
      console.error('❌ Error fetching assignments:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error message:', error.message);
      setSuccessDialogConfig({
        title: 'Error Loading Assignments',
        message: error.response?.data?.message || 'Failed to fetch assignments',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, isViewer, user?.role]);

  // Fetch individual completed leads
  const fetchCompletedLeads = useCallback(async () => {
    try {
      setLoadingCompletedLeads(true);
      console.log('🔄 Fetching individual completed leads from API...');
      console.log('📡 API endpoint: /api/assignments/completed-leads');
      
      const response = await clientAPI.getCompletedLeads();
      
      console.log('📥 Raw response:', response.data);
      
      if (response.data.success) {
        console.log('✅ Completed leads fetched successfully!');
        console.log('📊 Number of completed leads:', response.data.data.length);
        console.log('🔍 First 3 leads:', response.data.data.slice(0, 3));
        setCompletedLeads(response.data.data);
      } else {
        console.error('❌ API returned success: false for completed leads', response.data);
        setCompletedLeads([]);
      }
    } catch (error: any) {
      console.error('❌ Error fetching completed leads:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      // Don't show error dialog for completed leads to avoid disrupting the user experience
      // Just log it and show empty state
      setCompletedLeads([]);
    } finally {
      setLoadingCompletedLeads(false);
      console.log('🏁 Completed leads fetch finished');
    }
  }, []);

  const fetchCompletedWorkData = useCallback(async (assignmentId: string) => {
    try {
      const response = await clientAPI.getClientData();

      if (response.data.success && response.data.data.hasData) {
        // Filter to show only completed rows for this assignment's companies
        const assignment = [...assignments, ...completedAssignments].find(a => a._id === assignmentId);
        if (assignment) {
          const assignedCompanyNames = assignment.companies.map((c: any) => c.companyName);
          
          const allCompletedData = response.data.data.sheets.flatMap((sheet: any) => 
            sheet.data.filter((row: any) => {
              const companyName = row['Company Name'] || row['company_name'] || row['Company'] || '';
              const isAssignedCompany = assignedCompanyNames.includes(companyName);
              const isCompleted = row._isCompleted === true;
              const isCompletedByThisUser = row._completedBy === assignment.assignedTo?.username;
              
              return isAssignedCompany && isCompleted && isCompletedByThisUser;
            })
          );
          
          setCompletedWorkData(allCompletedData);
        }
      }
    } catch (error: any) {
      console.error('Error fetching completed work data:', error);
      setSuccessDialogConfig({
        title: 'Error Loading Data',
        message: 'Failed to fetch completed work data',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    }
  }, [assignments, completedAssignments]);

  const fetchCompanyLeadsData = useCallback(async (companyName: string) => {
    try {
      const response = await clientAPI.getClientData();

      if (response.data.success && response.data.data.hasData) {
        // Filter to show all rows for this specific company from Sheet 1 only (lead data)
        const leadDataSheet = response.data.data.sheets.find((sheet: any) => 
          sheet.fileType === 'csv' || 
          sheet.originalFileName.includes('Sheet 1') || 
          sheet.originalFileName.includes('Uploads Merged')
        );
        
        const allCompanyData = leadDataSheet ? 
          leadDataSheet.data.filter((row: any) => {
            const rowCompanyName = row['Company Name'] || row['company_name'] || row['Company'] || '';
            return rowCompanyName === companyName;
          }) : [];
        
        setSelectedCompanyLeads(allCompanyData);
        setSelectedCompanyName(companyName);
        setCompanyLeadsDialogOpen(true);
      } else {
        setSuccessDialogConfig({
          title: 'No Data Found',
          message: 'No data found for this company',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
      }
    } catch (error: any) {
      console.error('Error fetching company leads data:', error);
      setSuccessDialogConfig({
        title: 'Error Loading Company Data',
        message: 'Failed to fetch company leads data',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    }
  }, []);

  // NEW: Function to fetch leads for an assignment
  const fetchAssignmentLeads = useCallback(async (assignmentId: string) => {
    if (leadsData[assignmentId]) {
      return; // Already fetched
    }

    setLoadingLeads(prev => ({ ...prev, [assignmentId]: true }));
    
    try {
      const response = await clientAPI.getAssignmentLeads(assignmentId);
      
      if (response.data.success) {
        const leads = response.data.data.leads || [];
        setLeadsData(prev => ({ ...prev, [assignmentId]: leads }));
      } else {
        setSuccessDialogConfig({
          title: 'Error Loading Leads',
          message: response.data.message || 'Failed to fetch assignment leads',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
      }
    } catch (error: any) {
      setSuccessDialogConfig({
        title: 'Error Loading Leads',
        message: error.response?.data?.message || 'Failed to fetch assignment leads',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    } finally {
      setLoadingLeads(prev => ({ ...prev, [assignmentId]: false }));
    }
  }, [leadsData]);

  // NEW: Toggle row expansion
  const toggleRowExpansion = useCallback(async (assignmentId: string) => {
    const newExpandedRows = new Set(expandedRows);
    
    if (newExpandedRows.has(assignmentId)) {
      // Collapse
      newExpandedRows.delete(assignmentId);
      setExpandedRows(newExpandedRows);
    } else {
      // Expand
      newExpandedRows.add(assignmentId);
      setExpandedRows(newExpandedRows);
      
      // Fetch leads if not already fetched
      await fetchAssignmentLeads(assignmentId);
    }
  }, [expandedRows, fetchAssignmentLeads]);

  const fetchCompletedAssignments = useCallback(async () => {
    try {
      setLoading(true);
      
      let response;
      
      // Use viewer-specific endpoint for viewers, regular endpoint for others
      if (isViewer) {
        response = await clientAPI.getViewerAssignments();
        // Filter for completed assignments on the frontend
        if (response.data.success) {
          const completedAssignments = response.data.data.filter((assignment: Assignment) => 
            assignment.status === 'completed'
          );
          console.log('📋 Viewer completed assignments:', completedAssignments);
          console.log('🔍 All viewer assignments:', response.data.data.map((a: Assignment) => ({ 
            _id: a._id, 
            assignmentId: a.assignmentId, 
            status: a.status 
          })));
          setCompletedAssignments(completedAssignments);
        } else {
          throw new Error(response.data.message || 'Failed to fetch completed assignments');
        }
      } else {
        response = await clientAPI.getAssignments({
          status: 'completed',
          page: 1,
          limit: 100
        });

        if (response.data.success) {
          // Ensure we only show completed assignments
          const completedAssignments = (response.data.data || []).filter((assignment: Assignment) => 
            assignment.status === 'completed'
          );
          console.log('📋 Completed assignments:', completedAssignments);
          setCompletedAssignments(completedAssignments);
        } else {
          throw new Error(response.data.message || 'Failed to fetch completed assignments');
        }
      }
    } catch (error: any) {
      console.error('Error fetching completed assignments:', error);
      // Only show error for non-404 errors (404 means no completed assignments, which is normal)
      if (error.response?.status !== 404) {
        setSuccessDialogConfig({
          title: 'Error Loading Completed Assignments',
          message: error.response?.data?.message || 'Failed to fetch completed assignments',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
      }
      // Set empty array for completed assignments if there's an error
      setCompletedAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [isViewer]);

  useEffect(() => {
    if (currentTab === 0) {
      fetchCompletedAssignments();
      fetchCompletedLeads(); // Fetch individual completed leads for completed tab
    } else {
      fetchAssignments();
    }
  }, [currentTab, fetchAssignments, fetchCompletedAssignments, fetchCompletedLeads]);

  useEffect(() => {
    // Set view mode based on user role
    if (user?.role === 'admin') {
      setViewMode('admin');
    } else if (user?.role === 'viewer') {
      setViewMode('admin'); // Viewers use admin view (read-only) for better overview
    } else {
      setViewMode('sales');
    }
  }, [user?.role]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setSelectedAssignments([]);
    setSearchQuery('');
    setStatusFilter('');
    setPriorityFilter('');
    setExpandedRows(new Set());
  };

  // Handle refresh for completed tab
  const handleRefreshCompleted = async () => {
    await fetchCompletedAssignments();
    await fetchCompletedLeads();
  };

  const handleCompanyStatusUpdate = async (assignmentId: string, companyId: string, newStatus: string, notes?: string) => {
    // Prevent viewers from updating company status
    if (isViewer) {
      setSuccessDialogConfig({
        title: 'Access Restricted',
        message: 'Viewers cannot modify assignment status',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
      return;
    }
    
    try {
      const response = await axios.put(
        `${getApiUrl()}/api/assignments/${assignmentId}/company/${companyId}/status`,
        {
          status: newStatus,
          notes: notes || ''
        }
      );

      if (response.data.success) {
        setSuccessDialogConfig({
          title: 'Status Updated',
          message: 'Company status updated successfully',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
        fetchAssignments(); // Refresh the assignments list
      } else {
        throw new Error(response.data.message || 'Failed to update company status');
      }
    } catch (error: any) {
      console.error('Error updating company status:', error);
      setSuccessDialogConfig({
        title: 'Error Updating Status',
        message: error.response?.data?.message || 'Failed to update company status',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    }
  };

  const handleDeleteAssignments = async () => {
    if (!selectedAssignments.length) return;
    
    // Prevent viewers from deleting assignments
    if (isViewer) {
      setSuccessDialogConfig({
        title: 'Access Restricted',
        message: 'Viewers cannot delete assignments',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
      return;
    }

    try {
      // Check if we're deleting completed leads (tab 0) or assignments (tab 1)
      const isDeletingCompletedLeads = currentTab === 0;
      
      console.log(`🗑️ Deleting ${isDeletingCompletedLeads ? 'completed leads' : 'assignments'} with IDs:`, selectedAssignments);
      
      if (isDeletingCompletedLeads) {
        // Deleting individual completed leads
        console.log('🗑️ Deleting completed leads...');
        
        let successCount = 0;
        let failCount = 0;
        
        for (const leadEditId of selectedAssignments) {
          try {
            console.log(`🗑️ Deleting completed lead: ${leadEditId}`);
            await clientAPI.deleteCompletedLead(String(leadEditId));
            successCount++;
          } catch (error: any) {
            console.error(`❌ Failed to delete completed lead ${leadEditId}:`, error);
            failCount++;
          }
        }
        
        // Show success message
        if (successCount > 0) {
          setSuccessDialogConfig({
            title: failCount > 0 ? 'Partial Success' : 'Leads Deleted',
            message: failCount > 0 
              ? `Deleted ${successCount} lead(s). ${failCount} failed to delete.`
              : `Successfully deleted ${successCount} completed lead(s)`,
            autoCloseDelay: 3000,
          });
        } else {
          setSuccessDialogConfig({
            title: 'Error',
            message: 'Failed to delete completed leads',
            autoCloseDelay: 3000,
          });
        }
        setShowSuccessDialog(true);
        
        // Refresh completed leads
        setSelectedAssignments([]);
        setDeleteDialogOpen(false);
        await fetchCompletedLeads();
        
      } else {
        // Deleting assignments (existing logic)
        console.log('🗑️ Deleting assignments...');
        console.log('🗑️ Assignment objects being deleted:', assignments.filter((a: any) => selectedAssignments.includes(a._id)));
        
        // First, check which assignments exist before deleting
        const existingAssignments = [];
        const missingAssignments = [];
        
        for (const id of selectedAssignments) {
          try {
            console.log(`🔍 Checking if assignment ${id} exists...`);
            const checkResponse = await axios.get(`${getApiUrl()}/api/assignments/check/${id}`);
            console.log(`🔍 Assignment ${id} check result:`, checkResponse.data);
            
            if (!checkResponse.data.data.byId && !checkResponse.data.data.byCustomId) {
              console.log(`⚠️ Assignment ${id} not found in database, skipping deletion`);
              missingAssignments.push(id);
            } else {
              existingAssignments.push(id);
            }
          } catch (checkError: any) {
            console.log(`⚠️ Could not check assignment ${id}:`, checkError.message);
            missingAssignments.push(id);
          }
        }
        
        // Only delete assignments that exist
        if (existingAssignments.length > 0) {
          console.log(`🗑️ Deleting ${existingAssignments.length} existing assignments:`, existingAssignments);
          await Promise.all(
            existingAssignments.map(id => {
              console.log(`🗑️ Sending delete request for assignment ID: ${id}`);
              return axios.delete(`${getApiUrl()}/api/assignments/${id}`)
                .catch((error: any) => {
                  console.log(`❌ Failed to delete assignment ${id}:`, error.response?.status, error.message);
                  throw error;
                });
            })
          );
        }
        
        // Log results
        if (missingAssignments.length > 0) {
          console.log(`⚠️ Skipped ${missingAssignments.length} non-existent assignments:`, missingAssignments);
        }

        // Show appropriate success message based on what was actually deleted
        if (existingAssignments.length > 0 && missingAssignments.length === 0) {
          setSuccessDialogConfig({
            title: 'Assignments Deleted',
            message: `Successfully deleted ${existingAssignments.length} assignment(s)`,
            autoCloseDelay: 3000,
          });
        } else if (existingAssignments.length > 0 && missingAssignments.length > 0) {
          setSuccessDialogConfig({
            title: 'Partial Success',
            message: `Deleted ${existingAssignments.length} assignment(s). ${missingAssignments.length} were already removed.`,
            autoCloseDelay: 4000,
          });
        } else {
          setSuccessDialogConfig({
            title: 'No Changes',
            message: `All ${missingAssignments.length} assignment(s) were already removed.`,
            autoCloseDelay: 3000,
          });
        }
        setShowSuccessDialog(true);
        
        setSelectedAssignments([]);
        setDeleteDialogOpen(false);
        
        // Refresh both assignment lists to ensure UI is in sync
        console.log('🔄 Refreshing assignment lists after deletion...');
        
        // Always refresh both lists to keep them synchronized
        await Promise.all([
          fetchAssignments(),
          fetchCompletedAssignments()
        ]);
        
        console.log('✅ Assignment lists refreshed successfully');
      }
    } catch (error: any) {
      console.error('Error deleting:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      console.error('Full error:', JSON.stringify(error, null, 2));
      
      setSuccessDialogConfig({
        title: currentTab === 0 ? 'Error Deleting Leads' : 'Error Deleting Assignments',
        message: error.response?.data?.message || error.message || 'Failed to delete',
        autoCloseDelay: 5000,
      });
      setShowSuccessDialog(true);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'in_progress': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'urgent': return 'error';
      default: return 'default';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'lead_processing': return 'Lead Processing';
      case 'data_analysis': return 'Data Analysis';
      case 'data_entry': return 'Data Entry';
      case 'review': return 'Review';
      case 'research': return 'Research';
      default: return 'Other';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <PendingIcon />;
      case 'in_progress': return <InProgressIcon />;
      case 'completed': return <CompletedIcon />;
      case 'cancelled': return <CancelIcon />;
      default: return <TaskIcon />;
    }
  };

  // Transform completed leads into assignment-like format for display
  console.log('🔄 === TRANSFORMING COMPLETED LEADS ===');
  console.log('🔍 completedLeads state length:', completedLeads.length);
  console.log('🔍 First completed lead:', completedLeads.length > 0 ? completedLeads[0] : 'No leads');
const transformedCompletedLeads: Assignment[] = completedLeads.map((lead: CompletedLead) => {
  const originalData = lead.originalData || {};
  const firstName =
    originalData['First Name'] ||
    originalData['first_name'] ||
    originalData['FirstName'] ||
    originalData['FIRST_NAME'] ||
    originalData['Name'] ||
    '';
  const lastName =
    originalData['Last Name'] ||
    originalData['last_name'] ||
    originalData['LastName'] ||
    originalData['LAST_NAME'] ||
    '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || originalData['Full Name'] || originalData['full_name'] || originalData['Name'] || 'Unknown';
  const phone =
    originalData['Phone Number'] ||
    originalData['Phone'] ||
    originalData['phone'] ||
    originalData['Mobile'] ||
    originalData['mobile'] ||
    originalData['Contact Number'] ||
    originalData['contact_number'] ||
    '';
  const workEmail =
    originalData['Work Email'] ||
    originalData['Email'] ||
    originalData['email'] ||
    originalData['WorkEmail'] ||
    originalData['work_email'] ||
    '';

  return {
    _id: lead._id,
    assignmentId: lead.assignmentId,
    title: `Lead Completed: ${lead.leadCompanyName}`,
    description: `Lead ID: ${lead.leadId}`,
    assignedTo: lead.assignedTo || {
      _id: '',
      clientName: 'Unknown',
      username: 'Unknown',
      email: '',
      role: 'editor'
    },
    assignedBy: lead.assignedBy || {
      _id: '',
      clientName: 'Unknown',
      username: 'Unknown'
    },
    companyId: {
      _id: lead.assignmentObjectId || '',
      companyName: lead.companyName
    },
    priority: (lead.priority || 'medium') as 'low' | 'medium' | 'high' | 'urgent',
    status: 'completed' as const,
    category: (lead.category || 'lead_processing') as 'lead_processing' | 'data_analysis' | 'data_entry' | 'review' | 'research' | 'other',
    completedDate: lead.completedAt,
    completedAt: lead.completedAt,
    companies: (lead.companies && lead.companies.length > 0)
      ? lead.companies
      : [{
          companyId: lead.assignmentObjectId || '',
          companyName: lead.leadCompanyName || lead.companyName,
          country: originalData?.country || originalData?.Country || 'Unknown',
          status: 'completed',
          completedAt: lead.completedAt,
          completedLeads: 1,
          totalLeads: 1
        }],
    totalCompanies: 1,
    completedCompanies: 1,
    completionPercentage: 100,
    completedLeads: 1,
    totalLeads: 1,
    remainingLeads: 0,
    hasCompletedLeads: true,
    isFullyCompleted: true,
    createdAt: lead.createdAt,
    updatedAt: lead.completedAt,
    progressStatus: 'completed',
    isOverdue: false,
    completedBy: lead.completedBy,
    leadFullName: fullName || 'N/A',
    leadPhone: phone || 'N/A',
    leadWorkEmail: workEmail || 'N/A'
  } as Assignment;
});

  // Get current data based on tab - show individual completed leads in completed tab
  const currentData = currentTab === 0 ? transformedCompletedLeads : assignments;
  console.log('🔍 === CURRENT TAB DATA ===');
  console.log('🔍 Current tab index:', currentTab);
  console.log('🔍 Current tab name:', currentTab === 0 ? 'COMPLETED LEADS' : 'CURRENT ASSIGNMENTS');
  console.log('🔍 transformedCompletedLeads length:', transformedCompletedLeads.length);
  console.log('🔍 assignments length:', assignments.length);
  console.log('🔍 currentData (selected) length:', currentData.length);
  console.log('🔍 First 3 items in currentData:', currentData.slice(0, 3).map(item => ({ 
    id: item._id, 
    assignmentId: item.assignmentId,
    title: item.title 
  })));

  const filteredAssignments = currentData.filter((assignment) => {
    const matchesSearch = 
      assignment.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      assignment.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      assignment.assignedTo?.clientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      assignment.assignmentId.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = !statusFilter || assignment.status === statusFilter;
    const matchesPriority = !priorityFilter || assignment.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const getAssignmentStats = () => {
    if (currentTab === 0) {
      // For completed tab, show individual completed leads stats
      const total = completedLeads.length;
      const thisWeek = completedLeads.filter((lead: CompletedLead) => {
        const completedDate = new Date(lead.completedAt);
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        return completedDate >= oneWeekAgo;
      }).length;
      const thisMonth = completedLeads.filter((lead: CompletedLead) => {
        const completedDate = new Date(lead.completedAt);
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        return completedDate >= oneMonthAgo;
      }).length;
      
      return { total, thisWeek, thisMonth, completed: total, overdue: 0 };
    } else {
      // For current tab, show current stats
      const total = assignments.length;
      const pending = assignments.filter((a: any) => a.status === 'pending').length;
      const inProgress = assignments.filter((a: any) => a.status === 'in_progress').length;
      const completed = assignments.filter((a: any) => a.status === 'completed').length;
      const overdue = assignments.filter((a: any) => a.isOverdue).length;
      
      return { total, pending, inProgress, completed, overdue };
    }
  };

  const stats = getAssignmentStats();

  const allColumns: GridColDef[] = [
    // NEW: Expand/Collapse column
    {
      field: 'expand',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        const assignment = params.row as Assignment;
        const isExpanded = expandedRows.has(assignment._id);
        
        // Always show the expand icon so users can try to view leads
        return (
          <Tooltip title={isExpanded ? "Collapse lead details" : "Expand to see lead details"}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleRowExpansion(assignment._id);
              }}
              disabled={loadingLeads[assignment._id]}
              color={isExpanded ? "primary" : "default"}
            >
              {loadingLeads[assignment._id] ? (
                <CircularProgress size={20} />
              ) : isExpanded ? (
                <ExpandMoreIcon />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
          </Tooltip>
        );
      }
    },
    {
      field: 'assignmentId',
      headerName: 'Assignment ID',
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) => {
        const assignment = params.row as Assignment;
        const completedLeads = assignment.completedLeads || 0;
        const totalLeads = assignment.totalLeads || 0;
        const hasProgress = completedLeads > 0;
        
        // Check if this is a completed lead entry (in completed tab)
        const isCompletedLeadEntry = currentTab === 0 && completedLeads === 1 && totalLeads === 1;
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {params.value}
            </Typography>
            {hasProgress && (
              <Chip
                label={isCompletedLeadEntry ? "1 completed" : `${completedLeads}/${totalLeads}`}
                size="small"
                color="success"
                sx={{ fontSize: '0.7rem', height: '20px' }}
              />
            )}
          </Box>
        );
      }
    },
    {
      field: 'assignedTo',
      headerName: 'Assigned To',
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) => {
        // Handle null/undefined assignedTo or missing username
        if (!params.value || !params.value.username) {
          return (
            <Box className={`assigned-tasks-user-info-${isDarkMode ? 'dark' : 'light'}`}>
              <PersonIcon fontSize="small" />
              <Typography variant="body2" className={`assigned-tasks-user-name-${isDarkMode ? 'dark' : 'light'}`}>
                Unassigned
              </Typography>
            </Box>
          );
        }
        
        const isAssignedToCurrentUser = params.value?.username === user?.username;
        
        return (
          <Box className={`assigned-tasks-user-info-${isDarkMode ? 'dark' : 'light'}`}>
            <PersonIcon fontSize="small" />
            <Typography 
              variant="body2"
              className={isAssignedToCurrentUser && isViewer 
                ? `assigned-tasks-user-name-highlight-${isDarkMode ? 'dark' : 'light'}` 
                : `assigned-tasks-user-name-${isDarkMode ? 'dark' : 'light'}`
              }
            >
              {params.value?.clientName || 'Unknown User'}
              {isAssignedToCurrentUser && isViewer && (
                <Chip 
                  label="You" 
                  size="small" 
                  color="primary" 
                  variant="outlined" 
                  className={`assigned-tasks-user-chip-${isDarkMode ? 'dark' : 'light'}`}
                />
              )}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'assignedBy',
      headerName: 'Assigned By',
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) => (
        <Typography variant="body2">
          {params.value?.username || params.value?.clientName || 'Unknown'}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 120,
      renderCell: (params: any) => (
        <Chip
          icon={getStatusIcon(params.value)}
          label={params.value ? params.value.replace('_', ' ') : 'Unknown'}
          color={getStatusColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'completedBy',
      headerName: 'Completed By',
      flex: 1,
      minWidth: 160,
      valueGetter: (params: any) => params.row.completedBy || null,
      renderCell: (params: any) => {
        if (currentTab !== 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              --
            </Typography>
          );
        }
        
        const completedBy = params.value;
        if (!completedBy) {
          return (
            <Typography variant="body2" color="text.secondary">
              Unknown
            </Typography>
          );
        }
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PersonIcon fontSize="small" color="action" />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {completedBy.clientName || completedBy.username}
              </Typography>
              {completedBy.username && (
                <Typography variant="caption" color="text.secondary">
                  @{completedBy.username}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }
    },
    {
      field: 'priority',
      headerName: 'Priority',
      flex: 1,
      minWidth: 100,
      renderCell: (params: any) => (
        <Chip
          label={params.value}
          color={getPriorityColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'companies',
      headerName: 'Assigned Companies',
      flex: 2,
      minWidth: 250,
      renderCell: (params: any) => {
        const companies = params.row.companies || [];
        const displayCount = 2; // Show first 2 companies
        
        if (companies.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              No companies assigned
            </Typography>
          );
        }
        
        return (
          <Box sx={{ py: 0.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
              {companies.slice(0, displayCount).map((company: any, index: number) => (
                <Chip
                  key={index}
                  label={company.companyName}
                  size="small"
                  icon={<BusinessIcon />}
                  onClick={() => {
                    // Fetch and show company leads data
                    fetchCompanyLeadsData(company.companyName);
                  }}
                  sx={{ 
                    fontSize: '0.75rem',
                    height: 24,
                    '& .MuiChip-label': { px: 1 },
                    backgroundColor: '#2d3748', // Dark background
                    color: 'white', // White text
                    cursor: 'pointer', // Make it look clickable
                    '& .MuiChip-icon': { 
                      color: 'white' // White icon color
                    },
                    '&:hover': {
                      backgroundColor: '#1a202c' // Darker on hover
                    }
                  }}
                />
              ))}
              {companies.length > displayCount && (
                <Chip
                  label={`+${companies.length - displayCount} more`}
                  size="small"
                  sx={{ 
                    fontSize: '0.75rem', 
                    height: 24,
                    backgroundColor: '#4a5568', // Slightly lighter dark background
                    color: 'white', // White text
                    border: 'none',
                    '&:hover': {
                      backgroundColor: '#2d3748' // Darker on hover
                    }
                  }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {companies.length} compan{companies.length === 1 ? 'y' : 'ies'}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'createdAt',
      headerName: 'Assigned Date',
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) => {
        const dateText = params.value ? new Date(params.value).toLocaleDateString() : 'N/A';
        const assignedByUsername = params.row.assignedBy?.username;
        
        if (currentTab === 0) {
          return (
            <Box>
              <Typography variant="body2">
                {dateText}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {assignedByUsername ? `@${assignedByUsername}` : 'Unknown admin'}
              </Typography>
            </Box>
          );
        }
        
        return (
          <Typography variant="body2">
            {dateText}
          </Typography>
        );
      }
    },
  ];

  const completedLeadExtraColumns: GridColDef[] = [
    {
      field: 'leadFullName',
      headerName: 'Full Name',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: any) => (
        <Typography variant="body2">
          {params.value || 'N/A'}
        </Typography>
      )
    },
    {
      field: 'leadPhone',
      headerName: 'Phone',
      flex: 1,
      minWidth: 160,
      renderCell: (params: any) => {
        const phoneValue = params.value;
        if (!phoneValue || phoneValue === 'N/A') {
          return (
            <Typography variant="body2" color="text.secondary">
              N/A
            </Typography>
          );
        }
        const telLink = `tel:${phoneValue.replace(/[^0-9+]/g, '')}`;
        return (
          <Link
            href={telLink}
            underline="hover"
            sx={{ fontSize: '0.9rem' }}
          >
            {phoneValue}
          </Link>
        );
      }
    },
    {
      field: 'leadWorkEmail',
      headerName: 'Work Email',
      flex: 1.5,
      minWidth: 220,
      renderCell: (params: any) => {
        const emailValue = params.value;
        if (!emailValue || emailValue === 'N/A') {
          return (
            <Typography variant="body2" color="text.secondary">
              N/A
            </Typography>
          );
        }
        return (
          <Link
            href={`mailto:${emailValue}`}
            underline="hover"
            sx={{ fontSize: '0.9rem' }}
          >
            {emailValue}
          </Link>
        );
      }
    }
  ];

  // Filter columns based on view mode
  let columns = (viewMode === 'admin' || isViewer)
    ? allColumns
    : allColumns.filter(col => col.field !== 'assignedTo');

  if (currentTab === 0) {
    columns = columns.filter(col => !['expand', 'assignmentId', 'status', 'priority', 'assignedBy'].includes(col.field));
    const baseColumns = columns.filter(col => !['leadFullName', 'leadPhone', 'leadWorkEmail', 'companies', 'completedBy', 'createdAt'].includes(col.field));
    
    columns = [
      ...baseColumns,
      ...completedLeadExtraColumns,
      allColumns.find(col => col.field === 'companies')!,
      allColumns.find(col => col.field === 'completedBy')!,
      allColumns.find(col => col.field === 'createdAt')!
    ];
  }

  if (loading) {
    return (
      <Box className={`assigned-tasks-loading-container-${isDarkMode ? 'dark' : 'light'}`}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={`assigned-tasks-container-${isDarkMode ? 'dark' : 'light'}`}>
      <Box className={`assigned-tasks-header-${isDarkMode ? 'dark' : 'light'}`}>
        <Typography variant="h4" className={`assigned-tasks-title-${isDarkMode ? 'dark' : 'light'}`}>
          {isViewer 
            ? 'Company Assignments Overview'
            : viewMode === 'admin' 
              ? 'Assignment Management' 
              : 'My Assigned Tasks'
          }
        </Typography>
        <Typography variant="subtitle1" className={`assigned-tasks-subtitle-${isDarkMode ? 'dark' : 'light'}`}>
          {isViewer 
            ? 'Access all company assignments, past and present, and monitor progress across the company.'
            : viewMode === 'admin' 
              ? 'Manage and track all assignments across your organization' 
              : 'View and manage your assigned tasks and companies'
          }
        </Typography>
      </Box>

      {/* Tabs for Current vs Completed Assignments */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CompletedIcon />
                Completed Leads
                {completedLeads.length > 0 && (
                  <Chip 
                    label={completedLeads.length} 
                    size="small" 
                    color="success"
                    sx={{ ml: 0.5, height: '20px', fontSize: '0.75rem' }}
                  />
                )}
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InProgressIcon />
                Current Assignments
                {assignments.length > 0 && (
                  <Chip 
                    label={assignments.length} 
                    size="small" 
                    color="primary"
                    sx={{ ml: 0.5, height: '20px', fontSize: '0.75rem' }}
                  />
                )}
              </Box>
            } 
          />
        </Tabs>
      </Paper>

      {/* Statistics Cards */}
      {currentTab === 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TaskIcon color="primary" />
                  <div>
                    <Typography variant="h6">{stats.total}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isViewer ? 'Total Company Leads' : 'Total Leads Completed'}
                    </Typography>
                  </div>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TimeIcon color="info" />
                  <div>
                    <Typography variant="h6">{stats.thisWeek}</Typography>
                    <Typography variant="body2" color="text.secondary">Leads This Week</Typography>
                  </div>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ProgressIcon color="success" />
                  <div>
                    <Typography variant="h6">{stats.thisMonth}</Typography>
                    <Typography variant="body2" color="text.secondary">Leads This Month</Typography>
                  </div>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CompletedIcon color="success" />
                  <div>
                    <Typography variant="h6">{stats.completed}</Typography>
                    <Typography variant="body2" color="text.secondary">All Time Leads</Typography>
                  </div>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder={currentTab === 0 ? "Search completed leads..." : "Search assignments..."}
              value={searchQuery}
              onChange={(e: any) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e: any) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={priorityFilter}
                label="Priority"
                onChange={(e: any) => setPriorityFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={currentTab === 0 ? handleRefreshCompleted : fetchAssignments}
                className={`assigned-tasks-refresh-button ${isDarkMode ? 'assigned-tasks-refresh-button-dark' : 'assigned-tasks-refresh-button-light'}`}
              >
                Refresh
              </Button>
              {(!isViewer && (viewMode === 'admin' || currentTab === 0)) && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={selectedAssignments.length === 0}
                  className={`assigned-tasks-delete-button ${isDarkMode ? 'assigned-tasks-delete-button-dark' : 'assigned-tasks-delete-button-light'}`}
                >
                  Delete Selected
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Grid */}
      <Paper sx={{ width: '100%' }}>
        {(loading || loadingCompletedLeads) ? (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '400px',
              p: 4 
            }}
          >
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {currentTab === 0 ? 'Loading Completed Leads...' : 'Loading Assignments...'}
            </Typography>
          </Box>
        ) : filteredAssignments.length === 0 ? (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              p: 4 
            }}
          >
            {currentTab === 0 ? (
              <>
                <CompletedIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {loadingCompletedLeads ? 'Loading Completed Leads...' : 'No Completed Leads'}
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 600 }}>
                  {loadingCompletedLeads 
                    ? 'Please wait while we fetch your individual completed leads from all assignments...'
                    : 'No individual leads have been completed yet. Each lead you submit will appear here, even if the full assignment is not yet complete.'}
                </Typography>
              </>
            ) : (
              <>
                <TaskIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Current Assignments
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  No assignments are currently assigned to you. New assignments will appear here when they are created.
                </Typography>
              </>
            )}
          </Box>
        ) : (
          <>
            <DataGrid
              rows={filteredAssignments}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 10 }
                }
              }}
              pageSizeOptions={[10, 25, 50]}
              checkboxSelection={!isViewer && (viewMode === 'admin' || currentTab === 0)}
              disableRowSelectionOnClick
              onRowSelectionModelChange={(newSelection: GridRowSelectionModel) => {
                setSelectedAssignments(newSelection);
              }}
              rowSelectionModel={selectedAssignments}
              getRowId={(row: any) => row._id}
            />
            
            {/* Expanded Row Details - Lead List */}
            {Array.from(expandedRows).map(assignmentId => {
              const assignment = filteredAssignments.find(a => a._id === assignmentId);
              const leads = leadsData[assignmentId] || [];
              const isLoading = loadingLeads[assignmentId];
              
              if (!assignment) {
                return null;
              }
              
              return (
                <Box
                  key={assignmentId}
                  sx={{
                    borderTop: `2px solid ${theme.palette.primary.main}`,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    p: 2,
                    mt: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PeopleIcon />
                      Lead Details for {assignment.assignmentId}
                      {!isLoading && (
                        <Chip
                          label={`${assignment.completedLeads || 0} / ${assignment.totalLeads || 0} Completed`}
                          size="small"
                          color={assignment.isFullyCompleted ? "success" : "warning"}
                        />
                      )}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpansion(assignmentId)}
                      title="Collapse"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  
                  {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ ml: 2 }}>
                        Loading leads...
                      </Typography>
                    </Box>
                  ) : leads.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Lead ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Company Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Contact Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Call Outcome</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Lead Qualification</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Completed By</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Completed At</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {leads.map((lead, index) => (
                            <TableRow
                              key={lead.leadId || index}
                              sx={{
                                backgroundColor: lead._isCompleted
                                  ? theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)'
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                                }
                              }}
                            >
                              <TableCell>
                                <Chip
                                  label={lead._isCompleted ? 'Completed' : 'Pending'}
                                  size="small"
                                  color={lead._isCompleted ? 'success' : 'default'}
                                  icon={lead._isCompleted ? <CompletedIcon /> : <PendingIcon />}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                  {lead.leadId}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {lead.originalData?.['Company Name'] || lead.originalData?.companyName || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box>
                                  {lead.prevContactStatus && lead.prevContactStatus !== lead.newContactStatus && (
                                    <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary', display: 'block' }}>
                                      {cleanDisplayValue(lead.prevContactStatus, 'Contact Status')}
                                    </Typography>
                                  )}
                                  <Typography variant="body2" sx={{ color: lead._isCompleted ? 'success.main' : 'inherit' }}>
                                    {cleanDisplayValue(lead.newContactStatus, 'Contact Status') || 'N/A'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box>
                                  {lead.prevCallOutcome && lead.prevCallOutcome !== lead.newCallOutcome && (
                                    <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary', display: 'block' }}>
                                      {cleanDisplayValue(lead.prevCallOutcome, 'Call Outcome')}
                                    </Typography>
                                  )}
                                  <Typography variant="body2" sx={{ color: lead._isCompleted ? 'success.main' : 'inherit' }}>
                                    {cleanDisplayValue(lead.newCallOutcome, 'Call Outcome') || 'N/A'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box>
                                  {lead.prevLeadQualification && lead.prevLeadQualification !== lead.newLeadQualification && (
                                    <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary', display: 'block' }}>
                                      {cleanDisplayValue(lead.prevLeadQualification, 'Lead Qualification')}
                                    </Typography>
                                  )}
                                  <Typography variant="body2" sx={{ color: lead._isCompleted ? 'success.main' : 'inherit' }}>
                                    {cleanDisplayValue(lead.newLeadQualification, 'Lead Qualification') || 'N/A'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {lead._completedBy ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PersonIcon fontSize="small" color="action" />
                                    <Typography variant="body2">{lead._completedBy}</Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {lead._completedAt ? (
                                  <Typography variant="body2">
                                    {new Date(lead._completedAt).toLocaleDateString()}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto' }}>
                        <Typography variant="body2">
                          <strong>No lead data available for this assignment</strong>
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                          This could mean:
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ mt: 0.5, textAlign: 'left' }}>
                          • No leads have been uploaded for the company: <strong>{assignment.companyId?.companyName}</strong>
                          <br />
                          • The assignment doesn't have a specific lead company assigned
                          <br />
                          • You may need to contact your administrator
                        </Typography>
                      </Alert>
                    </Box>
                  )}
                </Box>
              );
            })}
          </>
        )}
      </Paper>

      {/* Assignment Details Dialog */}
      <Dialog 
        open={assignmentDialogOpen} 
        onClose={() => setAssignmentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TaskIcon />
            Assignment Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedAssignment ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  {selectedAssignment.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {selectedAssignment.description}
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Status:</Typography>
                  <Chip
                    icon={getStatusIcon(selectedAssignment.status)}
                    label={selectedAssignment.status.replace('_', ' ')}
                    color={getStatusColor(selectedAssignment.status)}
                    sx={{ mt: 1 }}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Priority:</Typography>
                  <Chip
                    label={selectedAssignment.priority}
                    color={getPriorityColor(selectedAssignment.priority)}
                    sx={{ mt: 1 }}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Progress:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={selectedAssignment.completionPercentage}
                      sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="body2">
                      {selectedAssignment.completionPercentage}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {selectedAssignment.completedCompanies}/{selectedAssignment.totalCompanies} companies completed
                  </Typography>
                </Box>

                {selectedAssignment.dueDate && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Due Date:</Typography>
                    <Typography variant="body2">
                      {new Date(selectedAssignment.dueDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Assigned Companies
                </Typography>
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {selectedAssignment.companies.map((company: any, index: number) => (
                    <Card key={index} variant="outlined" sx={{ mb: 1 }}>
                      <CardContent sx={{ py: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="subtitle2">
                              {company.companyName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {cleanDisplayValue(company.country, 'Country')}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={company.status.replace('_', ' ')}
                              color={getStatusColor(company.status)}
                              size="small"
                            />
                            {viewMode === 'sales' && company.status !== 'completed' && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  if (company.status === 'pending') {
                                    // Start button - navigate to sales dashboard
                                    // Get company slug from current URL
        const pathParts = window.location.pathname.split('/');
        const companySlug = pathParts[1];
        
        if (companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password'].includes(companySlug)) {
            navigate(`/${companySlug}/${user?.username}/dashboard`);
        } else {
            navigate('/client-signup');
        }
                                  } else {
                                    // Complete button - update status
                                    handleCompanyStatusUpdate(
                                      selectedAssignment._id,
                                      company.companyId,
                                      'completed'
                                    );
                                  }
                                }}
                              >
                                {company.status === 'pending' ? 'Start' : 'Complete'}
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Grid>
            </Grid>
          ) : (
            <p>No assignment selected</p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignmentDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Completed Work Data Sheet Dialog */}
      <Dialog 
        open={dataSheetDialogOpen} 
        onClose={() => setDataSheetDialogOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CompletedIcon />
              <Typography variant="h6">
                Completed Work: {selectedAssignment?.title}
              </Typography>
            </Box>
            <IconButton onClick={() => setDataSheetDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {completedWorkData.length > 0 ? (
            <Box sx={{ height: '70vh', width: '100%' }}>
              <Box sx={{ p: 2, backgroundColor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {completedWorkData.length} completed lead(s) with sales work
                </Typography>
              </Box>
              <TableContainer sx={{ height: 'calc(70vh - 60px)', overflow: 'auto' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {completedWorkData.length > 0 && Object.keys(completedWorkData[0])
                        .filter(key => !key.startsWith('_') || key === '_completedBy')
                        .map(column => (
                          <TableCell 
                            key={column}
                            sx={{ 
                              padding: '12px 8px',
                              textAlign: 'left',
                              fontWeight: 'bold',
                              fontSize: '0.875rem',
                              backgroundColor: theme.palette.background.paper
                            }}
                          >
                            {column === '_completedBy' ? 'Completed By' : column}
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {completedWorkData.map((row: any, index: number) => (
                      <TableRow key={index}>
                        {Object.keys(row)
                          .filter(key => !key.startsWith('_') || key === '_completedBy')
                          .map(column => (
                            <TableCell 
                              key={column}
                              sx={{ 
                                padding: '8px',
                                fontSize: '0.875rem',
                                whiteSpace: 'normal',
                                wordWrap: 'break-word',
                              }}
                              title={String(row[column] || '')}
                            >
                              {String(row[column] || '')}
                            </TableCell>
                          ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No completed work found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This assignment may not have any completed leads yet.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Company Leads Data Dialog */}
      <Dialog
        open={companyLeadsDialogOpen}
        onClose={() => setCompanyLeadsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '90vh',
            minHeight: '70vh',
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: theme.shadows[24],
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #2d2d2d 0%, #3a3a3a 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          borderRadius: '12px 12px 0 0',
          py: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <BusinessIcon sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
              <Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                  {selectedCompanyName}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  UAE • {selectedCompanyLeads.length} leads
                </Typography>
              </Box>
            </Box>

          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, overflow: 'auto' }}>
          <Box sx={{ p: 3 }}>
            {/* Leads Section */}
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 'bold', 
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <PersonIcon />
                  Leads ({selectedCompanyLeads.length})
                </Typography>
              </Box>
              
              {/* Leads Table */}
              {selectedCompanyLeads.length > 0 ? (
                <Box sx={{ 
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden'
                }}>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {selectedCompanyLeads.length > 0 && Object.keys(selectedCompanyLeads[0])
                            .filter(key => !key.startsWith('_') || key === '_completedBy')
                            .map(column => (
                              <TableCell 
                                key={column}
                                sx={{ 
                                  fontWeight: 'bold',
                                  backgroundColor: theme.palette.mode === 'dark' 
                                    ? 'rgba(255,255,255,0.1)' 
                                    : 'rgba(0,0,0,0.1)',
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.875rem',
                                  borderRight: `1px solid ${theme.palette.divider}`,
                                  padding: '12px 8px'
                                }}
                              >
                                {column === '_completedBy' ? 'Completed By' : column}
                              </TableCell>
                            ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedCompanyLeads.map((lead: any, index: number) => (
                          <TableRow 
                            key={index}
                            sx={{ 
                              '&:nth-of-type(odd)': { 
                                backgroundColor: theme.palette.mode === 'dark' 
                                  ? 'rgba(255,255,255,0.02)' 
                                  : 'rgba(0,0,0,0.02)' 
                              },
                              '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' 
                                  ? 'rgba(255,255,255,0.05)' 
                                  : 'rgba(0,0,0,0.05)' 
                              }
                            }}
                          >
                            {Object.keys(lead)
                              .filter(key => !key.startsWith('_') || key === '_completedBy')
                              .map(column => (
                                <TableCell 
                                  key={column}
                                  sx={{ 
                                    whiteSpace: 'normal',
                                    wordWrap: 'break-word',
                                    fontSize: '0.875rem',
                                    borderRight: `1px solid ${theme.palette.divider}`,
                                    padding: '8px 12px',
                                  }}
                                  title={String(lead[column] || '')}
                                >
                                  {String(lead[column] || '')}
                                </TableCell>
                              ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Table Footer */}
                  <Box sx={{ 
                    p: 2, 
                    borderTop: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.02)' 
                      : 'rgba(0,0,0,0.02)'
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      Showing {selectedCompanyLeads.length} leads
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">
                    No leads found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No data found for {selectedCompanyName}.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          borderTop: `1px solid ${theme.palette.divider}`,
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #2d2d2d 0%, #3a3a3a 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          borderRadius: '0 0 12px 12px',
          p: 3
        }}>
          <Button 
            onClick={() => setCompanyLeadsDialogOpen(false)} 
            variant="contained" 
            color="primary"
            sx={{ 
              px: 4, 
              py: 1.5,
              borderRadius: 2,
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '1rem'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedAssignments.length} {currentTab === 0 ? 'completed lead(s)' : 'assignment(s)'}? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDeleteAssignments} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={successDialogConfig.title}
        message={successDialogConfig.message}
        buttonText="Got it"
        autoCloseDelay={successDialogConfig.autoCloseDelay}
      />
    </Box>
  );
};

export default AssignedTasks;
